import React, {useState, useRef, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import news from '../../news';

const STORAGE_KEY = 'raapustin.lastSeenNewsId';

function getLastSeen() {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function setLastSeen(id) {
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
}

export default function NewsButton() {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [lastSeen, setLastSeenState] = useState(() => getLastSeen());
    const ref = useRef(null);

    const latestId = news.length > 0 ? news[0].id : null;

    // First-time visitors: silently catch up to the latest entry so they
    // don't see a "you have unread news!" dot for items shipped before
    // they ever opened the site.
    useEffect(() => {
        if (lastSeen === null && latestId) {
            setLastSeen(latestId);
            setLastSeenState(latestId);
        }
    }, [lastSeen, latestId]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [open]);

    // Close on Escape.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    if (news.length === 0) return null;

    const hasUnread = lastSeen !== null && lastSeen !== latestId;

    function handleOpen() {
        setOpen(o => !o);
        if (latestId && lastSeen !== latestId) {
            setLastSeen(latestId);
            setLastSeenState(latestId);
        }
    }

    const btnStyle = {
        display: 'flex', alignItems: 'center', gap: 5,
        background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 6, padding: '4px 10px',
        color: '#eee', cursor: 'pointer',
        fontSize: 13, fontFamily: 'sans-serif',
        lineHeight: 1, whiteSpace: 'nowrap',
        position: 'relative',
    };

    return (
        <div ref={ref} style={{position: 'relative', zIndex: 100}}>
            <button
                style={btnStyle}
                onClick={handleOpen}
                aria-label={t('news.title', "What's new")}
                title={t('news.title', "What's new")}
            >
                <span style={{fontSize: 16}}>📰</span>
                <span>{t('news.button', 'News')}</span>
                {hasUnread && (
                    <span
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            top: -3, right: -3,
                            width: 9, height: 9,
                            background: '#e84a4a',
                            border: '1.5px solid #1a1a2e',
                            borderRadius: '50%',
                        }}
                    />
                )}
            </button>
            {open && (
                <div style={{
                    position: 'fixed', top: 50, right: 8,
                    background: '#23233a', border: '1px solid #555',
                    borderRadius: 10, padding: 0,
                    width: 'calc(100vw - 16px)', maxWidth: 380,
                    maxHeight: '70vh', overflowY: 'auto',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
                    color: '#eee',
                    zIndex: 1000,
                }}>
                    <div style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #3a3a55',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        position: 'sticky', top: 0,
                        background: '#23233a',
                    }}>
                        <strong style={{fontSize: 14}}>{t('news.title', "What's new")}</strong>
                        <button
                            onClick={() => setOpen(false)}
                            aria-label={t('news.close', 'Close')}
                            style={{
                                background: 'transparent', border: 'none',
                                color: '#aab', cursor: 'pointer',
                                fontSize: 18, lineHeight: 1, padding: 4,
                            }}
                        >×</button>
                    </div>
                    <ul style={{listStyle: 'none', margin: 0, padding: 0}}>
                        {news.map(item => (
                            <li
                                key={item.id}
                                style={{
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #2e2e48',
                                }}
                            >
                                <div style={{
                                    display: 'flex', alignItems: 'baseline',
                                    justifyContent: 'space-between', gap: 8,
                                    marginBottom: 4,
                                }}>
                                    <span style={{fontWeight: 600, fontSize: 14}}>{item.title}</span>
                                    <span style={{color: '#889', fontSize: 11, fontFamily: 'monospace'}}>
                                        {item.date}
                                    </span>
                                </div>
                                <div style={{fontSize: 13, lineHeight: 1.4, color: '#cdd', whiteSpace: 'pre-line'}}>
                                    {item.summary}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
